import { SceneManager } from './SceneManager.js'
import { ScrollController } from './ScrollController.js'
import { CameraController } from './CameraController.js'
import { HeroSection } from '../sections/HeroSection.js'
import { ProjectsSection } from '../sections/ProjectsSection.js'
import { ExperienceSection } from '../sections/ExperienceSection.js'
import { CTASection } from '../sections/CTASection.js'

export class App {
  constructor(canvas) {
    this.canvas = canvas
    this.sizes = {
      width: window.innerWidth,
      height: window.innerHeight
    }
    
    // Core systems
    this.sceneManager = new SceneManager(canvas)
    this.scrollController = new ScrollController(this.sceneManager.scene /* , this.sceneManager.scene.fog */)
    this.cameraController = new CameraController(this.sizes)
    this.sceneManager.scene.add(this.cameraController.getCamera())
    
    // Sections
    this.sections = []
    this.currentSectionIndex = 0
    
    // Animation timing
    this.lastTime = performance.now()
    
    // Bind callbacks
    this.scrollController.onSectionChange = this.onSectionChange.bind(this)
    
    // Resize handler
    window.addEventListener('resize', this.onResize.bind(this))
    
    // Initialize
    this.init()
    this.setupNavigation()
  }
  
  setupNavigation() {
    // Setup nav dot click handlers
    document.querySelectorAll('.nav-dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const sectionIndex = parseInt(dot.dataset.section)
        this.navigateToSection(sectionIndex)
      })
    })
  }
  
  navigateToSection(index) {
    // Hardcoded camera target positions for each section
    // Camera is 5 units in front of each cube
    // [Hero, Projects, Experience, CTA]
    const cameraTargets = [5, -20, -45, -70]
    
    // Set camera target directly — ScrollController.update() will drive
    // section changes naturally as the camera moves, keeping click and
    // scroll transitions consistent.
    this.scrollController.targetZ = cameraTargets[index]
  }
  
  updateActiveDot() {
    const index = this.currentSectionIndex
    document.querySelectorAll('.nav-dot').forEach((dot, i) => {
      dot.classList.toggle('active', i === index)
    })
  }
  
  async init() {
    // Start indeterminate load bar animation (0-60% loop)
    const loadBarFill = document.getElementById('loading-bar-fill')
    const loadPercent = document.getElementById('loading-percent')
    if (loadBarFill) loadBarFill.classList.add('indeterminate')
    
    // Animate percentage text from 0% to 60% over 5 seconds
    const percentStartTime = performance.now()
    let percentRaf = null
    const updatePercent = () => {
      if (!loadPercent || loadPercent.textContent === '100%') return
      const elapsed = (performance.now() - percentStartTime) / 1000
      const t = Math.min(elapsed / 5, 1)
      const eased = t < 0.5
        ? 2 * t * t
        : 1 - Math.pow(-2 * t + 2, 2) / 2
      const percent = Math.round(eased * 60)
      loadPercent.textContent = percent + '%'
      if (t < 1) {
        percentRaf = requestAnimationFrame(updatePercent)
      }
    }
    percentRaf = requestAnimationFrame(updatePercent)
    
    try {
      // Create sections at fixed positions (with section indices for background z-layering)
      this.sections = [
        new HeroSection(this.sceneManager.scene, 0, 0),
        new ProjectsSection(this.sceneManager.scene, -25, 1),
        new ExperienceSection(this.sceneManager.scene, -50, 2),
        new CTASection(this.sceneManager.scene, -75, 3)
      ]
      
      // Build all sections with camera and renderer (await async builds)
      const camera = this.cameraController.getCamera()
      const renderer = this.sceneManager.renderer
      await Promise.all(this.sections.map(section => section.build(camera, renderer)))
      
      // Inject camera into HeroSection for lantern interaction
      if (this.sections[0].setCamera) {
        this.sections[0].setCamera(this.cameraController.getCamera(), this.canvas)
      }
      
      // Initialize lanterns asynchronously
      if (this.sections[0].initLanterns) {
        await this.sections[0].initLanterns()
      }
    } catch (err) {
      console.error('Failed to initialize app:', err)
      if (loadPercent) loadPercent.textContent = 'Error'
      return
    }
    
    // Loading complete — jump bar and percent to 100%
    cancelAnimationFrame(percentRaf)
    if (loadBarFill) {
      loadBarFill.classList.remove('indeterminate')
      loadBarFill.classList.add('complete')
    }
    if (loadPercent) loadPercent.textContent = '100%'
    
    // Pre-warm: force camera through all sections to compile shaders & upload textures
    const camera = this.cameraController.getCamera()
    const warmPositions = [5, -20, -45, -70]
    for (const z of warmPositions) {
      camera.position.z = z
      this.cameraController.update(z)
      // Update section visibility & backgrounds for this position
      this.sections.forEach(section => {
        section.updateBackgroundOpacity(z)
      })
      // Render a few frames to ensure GPU work is done
      for (let i = 0; i < 3; i++) {
        this.sceneManager.render(camera)
      }
    }
    // Reset to hero position
    camera.position.z = 5
    this.cameraController.update(5)
    
    // Brief pause so the 100% fill is visible, then hide overlay
    await new Promise(resolve => setTimeout(resolve, 500))
    
    const loadingScreen = document.getElementById('loading-screen')
    if (loadingScreen) {
      loadingScreen.classList.add('hidden')
    }
    
    // Enter first section
    this.sections[0].onEnter()
    
    // Start animation loop
    this.tick()
  }
  
  onSectionChange(index, sectionData) {
    // Exit previous section
    if (this.sections[this.currentSectionIndex]) {
      this.sections[this.currentSectionIndex].onExit()
      this.exitingSectionIndex = this.currentSectionIndex
    }
    
    this.currentSectionIndex = index
    this.updateActiveDot()
    
    // Enter new section
    if (this.sections[this.currentSectionIndex]) {
      this.sections[this.currentSectionIndex].onEnter()
    }
  }
  
  onResize() {
    this.sizes.width = window.innerWidth
    this.sizes.height = window.innerHeight
    
    this.sceneManager.onResize()
    this.cameraController.onResize(this.sizes)
    
    // Recalculate background plane geometry for all sections
    // (critical for mobile orientation changes: portrait → landscape)
    this.sections.forEach(section => {
      if (section.onResize) section.onResize()
    })
  }
  
  tick() {
    // Calculate real delta time
    const currentTime = performance.now()
    const delta = (currentTime - this.lastTime) / 1000
    this.lastTime = currentTime
    
    // Get camera Z position
    const cameraZ = this.scrollController.update()
    
    // Update camera
    this.cameraController.update(cameraZ)
    
    // Store camera reference for sections that need it
    this.sceneManager.scene.userData.camera = this.cameraController.getCamera()
    
    // Update current section animations
    const currentSection = this.sections[this.currentSectionIndex]
    if (currentSection && currentSection.isActive) {
      const isCameraSettled = Math.abs(this.scrollController.cameraZ - this.scrollController.targetZ) < 0.1
      currentSection.update(delta, 0, cameraZ, isCameraSettled)
    }
    
    // Update exiting section exit animations
    if (this.exitingSectionIndex !== null && this.exitingSectionIndex !== this.currentSectionIndex) {
      const exitingSection = this.sections[this.exitingSectionIndex]
      if (exitingSection && exitingSection.isExiting) {
        exitingSection.update(delta, 0, cameraZ, false)
      } else {
        this.exitingSectionIndex = null
      }
    }
    
    // Update all section background opacities based on camera position
    this.sections.forEach(section => {
      section.updateBackgroundOpacity(this.cameraController.camera.position.z)
    })
    
    // Render current section and any exiting sections
    this.sections.forEach(section => {
      if (section.group) {
        const isCurrent = section.sectionIndex === this.currentSectionIndex
        const isExiting = section.sectionIndex === this.exitingSectionIndex && section.isExiting
        section.group.visible = isCurrent || isExiting
      }
    })
    
    // Update nav dots (even during warp)
    this.updateActiveDot()
    
    // Render
    this.sceneManager.render(this.cameraController.getCamera())
    
    // Continue loop
    window.requestAnimationFrame(this.tick.bind(this))
  }
  
  destroy() {
    this.scrollController.destroy()
    this.sections.forEach(section => section.destroy())
  }
}
